import { PlanType, usePaymentPlans } from '@va/dashboard/api-client/pricing-plans.api';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { useTranslate } from '@va/localization';
import { appHistory } from '@va/shared/router';
import { setUpgradePlanIndex, setUpgradePlanSubscriptionType } from '@va/standalone/shared/actions';
import { SubscriptionTypeEnum } from '@va/standalone/shared/constants';
import {
  Button,
  fontWeights,
  Heading3,
  Paragraph,
  paragraphSizes,
  TabbedButton,
  TabbedButtonProps,
} from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { PricingPlanTile } from './PricingPlanTile';
import { getRelevantPlans } from './utils';

type PricingPlanSuggestionModalProps = {
  getRecommendedPlan: (plans: Record<number, PlanType>) => PlanType | null;
  getPlanFeature?: (plan: PlanType) => string;
  hint?: string;
  title?: string;
  subtitle?: ReactNode;
  recommendedBasedOn?: string;
  onClose?: () => void;
  isLoading?: boolean;
};

export const PricingPlanSuggestionModal = ({
  getRecommendedPlan,
  getPlanFeature,
  hint,
  subtitle,
  title,
  recommendedBasedOn,
  onClose,
  isLoading: isLoadingProp,
}: PricingPlanSuggestionModalProps) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const websiteId = useSelector(getInstanceId);

  const [isOpen, setIsOpen] = useState(true);
  const [subscriptionType, setSubscriptionType] = useState(SubscriptionTypeEnum.YEARLY);

  const { data: paymentPlans, isLoading: isPaymentPlansLoading } = usePaymentPlans(true);
  const isLoading = isLoadingProp || isPaymentPlansLoading;

  const closeModal = useCallback(() => {
    onClose?.();
    setIsOpen(false);
  }, [onClose]);

  const tabs = useMemo<TabbedButtonProps['buttonOptions']>(
    () => [
      {
        value: SubscriptionTypeEnum.MONTHLY,
        label: translate('modals.freeTrialEnding.tabs.monthly'),
        disabled: isLoading,
      },
      {
        value: SubscriptionTypeEnum.YEARLY,
        label: translate('modals.freeTrialEnding.tabs.yearly'),
        disabled: isLoading,
      },
    ],
    [translate, isLoading],
  );

  const { recommendedPlan, otherPlans } = useMemo(() => {
    if (!paymentPlans) return { recommendedPlan: null, otherPlans: [] };

    const plans = paymentPlans[subscriptionType];
    const recommendedPlan = getRecommendedPlan(plans);
    const otherPlans = getRelevantPlans(recommendedPlan, plans);

    return {
      recommendedPlan,
      otherPlans,
    };
  }, [getRecommendedPlan, paymentPlans, subscriptionType]);

  const [selectedPlanIndex, setSelectedPlanIndex] = useState(recommendedPlan?.index);

  useEffect(() => {
    if (selectedPlanIndex !== undefined || recommendedPlan === null) return;

    setSelectedPlanIndex(recommendedPlan.index);
  }, [recommendedPlan, selectedPlanIndex, setSelectedPlanIndex]);

  const handleSubmit = useCallback(async () => {
    dispatch(setUpgradePlanIndex(selectedPlanIndex));
    dispatch(setUpgradePlanSubscriptionType(subscriptionType));

    closeModal();

    appHistory.push(`/website/${websiteId}/upgrade`);
  }, [closeModal, dispatch, selectedPlanIndex, subscriptionType, websiteId]);

  useEffect(() => {
    if (selectedPlanIndex !== undefined || recommendedPlan === null) return;

    setSelectedPlanIndex(recommendedPlan?.index);
  }, [recommendedPlan, selectedPlanIndex]);

  const onAllPlansClick = useCallback(async () => {
    closeModal();
    appHistory.push(`/website/${websiteId}/upgrade`);
  }, [closeModal, websiteId]);

  return (
    <ModalWrapper isModalOpen={isOpen} closeModal={closeModal} contentClassName='sm:rounded-24 rounded-30 sm:w-full'>
      <div className='max-w-[45rem] sm:p-3 sm:pt-18px p-18px pt-6 overflow-hidden'>
        <Heading3 className='text-center text-wrap' weight={fontWeights.semibold}>
          {title}
        </Heading3>
        <Paragraph className='text-center' weight={fontWeights.medium}>
          {subtitle}
        </Paragraph>
        <div>
          <TabbedButton
            className='my-3 h-12!'
            buttonOptions={tabs}
            onChange={setSubscriptionType}
            selectedValue={subscriptionType}
          />
          {isLoading && !recommendedPlan && (
            <Skeleton
              className='block rounded-12'
              containerClassName='block w-full mb-1.5'
              inline
              count={1}
              height={90}
            />
          )}
          {recommendedPlan && (
            <PricingPlanTile
              selected={selectedPlanIndex === recommendedPlan.index}
              planFeature={getPlanFeature?.(recommendedPlan)}
              title={recommendedPlan.name}
              onClick={() => setSelectedPlanIndex(recommendedPlan.index)}
              className='mb-1.5'
              price={recommendedPlan.monthlyPrice}
              recommended
              recommendedBasedOn={recommendedBasedOn}
            />
          )}
          <div className='flex sm:flex-col gap-1.5 overflow-hidden mb-3'>
            {isLoading && !otherPlans.length && (
              <Skeleton
                className='block rounded-12'
                containerClassName='block flex gap-1.5 w-135'
                count={2}
                height={82}
                inline
              />
            )}
            {otherPlans.map((plan, index) => (
              <PricingPlanTile
                key={index}
                selected={plan.index === selectedPlanIndex}
                planFeature={getPlanFeature?.(plan)}
                onClick={() => setSelectedPlanIndex(plan.index)}
                title={plan.name}
                price={plan.monthlyPrice}
              />
            ))}
            <Button
              className='rounded-18'
              textClasses='text-wrap'
              text={translate('modals.freeTrialEnding.allPlansBtn')}
              color='secondary'
              onClick={onAllPlansClick}
              disabled={isLoading}
            />
          </div>
          <div className='px-18px py-3 bg-gray-wild-sand rounded-12'>
            <Paragraph size={paragraphSizes.tiny}>
              <span className='font-semibold'>{translate('modals.freeTrialEnding.hint')}</span>&nbsp;
              {hint}
            </Paragraph>
          </div>
        </div>
        <div className='flex gap-3 mt-3'>
          <Button
            className='flex-1'
            color='quinary'
            text={translate('modals.freeTrialEnding.closeBtn')}
            onClick={closeModal}
          />
          <Button
            className='flex-1'
            text={translate('modals.freeTrialEnding.submitBtn')}
            disabled={isLoading}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};
